import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorBox, Spinner } from "../../components/bits";
import { Icon } from "../../components/icons";
import { Forbidden, StatCard, useOrgData } from "./shared";

export function OverviewTab() {
  const { data, error, forbidden, reload } = useOrgData((orgId) => api.admin.overview(orgId));
  if (forbidden) return <Forbidden />;
  if (error && !data) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Spinner />;
  const { counts, health, instance, settings } = data;
  const warnings: { text: string; to: string }[] = [];
  if (health.connections_needing_auth)
    warnings.push({
      text: `${health.connections_needing_auth} connection${health.connections_needing_auth === 1 ? " needs" : "s need"} to be signed in again`,
      to: "/admin/sync?problems=1",
    });
  if (health.connections_error)
    warnings.push({
      text: `${health.connections_error} connection${health.connections_error === 1 ? " has" : "s have"} errors`,
      to: "/admin/sync?problems=1",
    });
  if (health.bindings_error)
    warnings.push({
      text: `${health.bindings_error} sync binding${health.bindings_error === 1 ? " is" : "s are"} failing`,
      to: "/admin/sync?problems=1",
    });

  return (
    <div className="admin-overview">
      <section aria-labelledby="health-title" className="admin-section">
        <h2 id="health-title">Health</h2>
        {warnings.length === 0 ? (
          <p className="health-ok">
            <span className="health ok">
              <span className="dot" aria-hidden="true" />
              All good
            </span>{" "}
            <span className="muted small">No connection or sync problems.</span>
          </p>
        ) : (
          <ul className="warn-list">
            {warnings.map((w) => (
              <li key={w.text}>
                <Link to={w.to} className="warn-item">
                  <Icon name="warning" />
                  <span>{w.text}</span>
                  <span className="muted small">Open sync →</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="counts-title" className="admin-section">
        <h2 id="counts-title">Organization</h2>
        <div className="stat-grid">
          <StatCard label="Members" value={counts.members} />
          <StatCard label="Guests" value={counts.guests} />
          <StatCard label="Ghost people" value={counts.ghosts} />
          <StatCard label="Spaces" value={counts.spaces} />
          <StatCard label="Collections" value={counts.collections} />
          <StatCard label="Items" value={counts.items} />
          <StatCard label="Connections" value={counts.connections} />
          <StatCard label="Sync bindings" value={counts.bindings} />
        </div>
      </section>

      <section aria-labelledby="inst-title" className="admin-section">
        <h2 id="inst-title">Instance</h2>
        <dl className="kv">
          <dt>Name</dt>
          <dd>{instance.name}</dd>
          <dt>Mode</dt>
          <dd>{instance.mode}</dd>
          <dt>Version</dt>
          <dd>
            <code>{instance.version}</code>
          </dd>
          <dt>Default time zone</dt>
          <dd>{settings.default_timezone}</dd>
          <dt>Open registration</dt>
          <dd>{settings.open_registration ? "Yes" : "No"}</dd>
          <dt>User connections</dt>
          <dd>{settings.allow_user_connections ? "Allowed" : "Disabled"}</dd>
        </dl>
        <Link to="/admin/settings" className="small">
          Change settings →
        </Link>
      </section>
    </div>
  );
}
